import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { getIsAnonymous } from 'shared/tokens';

import routes from '@/assets/constants/routes-no.ts';
import Loader from '@/components/Loader/index.ts';
import { EconomySection } from '@/components/marketing/economy-section/index.ts';
import { Section } from '@/components/marketing/section/index.ts';
import { SplitHeroSection } from '@/components/marketing/split-hero-section/index.ts';
import { TestimonialsSection } from '@/components/marketing/testimonials-section/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { testimonialsQuery } from '@/lib/api/requests.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import { SEO } from '@/lib/seo.tsx';
import { withSearchParams } from '@/lib/utils.ts';
import { useAuth } from '@/services/Auth/index.ts';
import { Button } from '@/ui/button-2.tsx';

import type { Route } from './+types/Login';
import heroImage from './hero-image.svg';

export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const testimonials = await queryClient.query(testimonialsQuery());

  return { testimonials };
}

export default function Login({ loaderData }: Route.ComponentProps) {
  const { login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void getIsAnonymous().then((isAnonymous) => {
      if (cancelled || isAnonymous) {
        return;
      }

      setRedirecting(true);
      const returnTo = params.get('return_to');
      const destination =
        returnTo?.startsWith('/') && !returnTo.startsWith('//')
          ? returnTo
          : routes.MY_SCOOPR_DASHBOARD;
      void navigate(destination, { replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [navigate, params]);

  const loginAction = async () => {
    sendUserPortalEvent('user_login_started');
    const returnTo = params.get('return_to') || routes.MY_SCOOPR_DASHBOARD;
    await login('SCRIVE', withSearchParams(returnTo, { from: 'my-scoopr' }));
  };

  return (
    <div className="flex w-full flex-col bg-white text-v1-background-200">
      <SEO
        title="Logg inn på Min Scoopr"
        description="Logg inn på Min Scoopr med BankID. Se kredittscore, sjekk usikret gjeld og få et gratis estimat av boligverdien din."
        path={routes.MY_SCOOPR_LOGIN}
        noindex
      />

      {redirecting ? (
        <Loader />
      ) : (
        <article className="flex flex-col gap-8 sm:gap-15 md:mt-15">
          <SplitHeroSection.Root>
            <SplitHeroSection.Content>
              <SplitHeroSection.Title>Min Scoopr</SplitHeroSection.Title>

              <SplitHeroSection.List>
                <SplitHeroSection.ListItem>
                  Se kredittscore og sjekk om du har betalingsanmerkninger
                </SplitHeroSection.ListItem>
                <SplitHeroSection.ListItem>
                  Få oversikt over usikret gjeld og sjekk ditt sparepotensiale
                </SplitHeroSection.ListItem>
                <SplitHeroSection.ListItem>
                  <span>
                    <b>Gratis</b> estimat av din boligverdi
                  </span>
                </SplitHeroSection.ListItem>
              </SplitHeroSection.List>

              <Button
                color="yellow"
                className="mb-4 w-full max-w-sm md:mb-6 lg:mb-8"
                action={loginAction}
              >
                Fortsett med BankID
              </Button>

              <p className="max-w-md text-center typography-v1-body3">
                Ved å fortsette aksepterer jeg Scoopr sine{' '}
                <Link
                  to={routes.TERMS_OF_USE_MIN_SCOOPR}
                  className="text-v1-primary-100 hover:underline"
                >
                  brukervilkår
                </Link>{' '}
                og{' '}
                <Link to={routes.PRIVACY_STATEMENT} className="text-v1-primary-100 hover:underline">
                  personvernerklæring
                </Link>
              </p>
            </SplitHeroSection.Content>

            <SplitHeroSection.ImageContainer className="max-sm:min-h-120">
              <SplitHeroSection.Image src={heroImage} alt="Min Scoopr" width={500} height={573} />
            </SplitHeroSection.ImageContainer>
          </SplitHeroSection.Root>

          <Section.Container className="bg-white text-v1-background-200">
            <Section.Content className="py-8">
              <div className="flex max-w-2xl flex-col gap-8">
                <h2 className="typography-v1-h2 text-pretty">
                  Hvordan få oversikt over egen økonomi?
                </h2>
                <p className="typography-v1-body2">
                  Med Min Scoopr får du tilgang til smarte verktøy som gjør det enklere å forstå og
                  følge opp egen økonomi. På ett sted kan du ta kredittsjekk, sjekke gjelden din og
                  få oversikt over boligverdien din, slik at du alltid har bedre kontroll på den
                  økonomiske situasjonen din.
                </p>
                <p className="typography-v1-body2">
                  Ved å samle viktig økonomisk informasjon i én løsning blir det enklere å planlegge
                  fremover, følge utviklingen i økonomien og ta smartere økonomiske valg. Enten du
                  ønsker bedre oversikt over gjeld, vurderer refinansiering eller bare vil holde
                  kontroll på privatøkonomien, gir Min Scoopr deg innsikten du trenger.
                </p>
              </div>
            </Section.Content>
          </Section.Container>

          <TestimonialsSection data={loaderData.testimonials} />
          <EconomySection />
        </article>
      )}
    </div>
  );
}
